import type { PositionedItem } from '../../PageConfigProvider/types/item';
import type { TextChartContentMap } from '../../TextChartContentProvider/TextChartContentProvider';
import type { ChartExportInfo } from './chart-export-types';

const MIXED_STATS_REGISTRY_KEY = 'mixed-stats';

export function buildOrderedPptExportCharts({
  items,
  registry,
  aiSummaryState,
  getTextExportForItem,
  getAiExportForItem,
}: {
  items: PositionedItem[];
  registry: Map<string, ChartExportInfo>;
  aiSummaryState: {
    summary: string;
    loading: boolean;
    error: string | null;
  };
  getTextExportForItem: (item: PositionedItem) => ChartExportInfo | null;
  getAiExportForItem: (
    item: PositionedItem,
    summaryText: string
  ) => ChartExportInfo | null;
}): ChartExportInfo[] {
  const out: ChartExportInfo[] = [];

  const sortedItems = [...items].sort(
    (a, b) => a.position.y - b.position.y || a.position.x - b.position.x
  );

  for (const item of sortedItems) {
    const chartType = item.plot?.chartType;
    if (!chartType) {
      continue;
    }

    if (chartType === 'text') {
      const slide = getTextExportForItem(item);
      if (slide?.textContent?.trim()) {
        out.push({ ...slide, position: item.position, itemId: item.id });
      }
      continue;
    }

    if (chartType === 'ai-summary') {
      if (aiSummaryState.loading) {
        continue;
      }
      const body = aiSummaryState.error !== null ? '' : aiSummaryState.summary;
      if (!body.trim()) {
        continue;
      }
      const slide = getAiExportForItem(item, body);
      if (slide?.textContent?.trim()) {
        out.push({ ...slide, position: item.position, itemId: item.id });
      }
      continue;
    }

    if (chartType === 'mixed-stats') {
      const slide = registry.get(MIXED_STATS_REGISTRY_KEY);
      if (slide) {
        out.push({ ...slide, position: item.position, itemId: item.id });
      }
      continue;
    }

    const slide = registry.get(item.id);
    if (slide) {
      out.push({ ...slide, position: item.position, itemId: item.id });
    }
  }

  return out;
}

export function countAuxiliaryPptBlocking({
  items,
  textCharts,
  aiSummaryState,
}: {
  items: PositionedItem[];
  textCharts: TextChartContentMap;
  aiSummaryState: { loading: boolean };
}): number {
  let pending = 0;

  for (const item of items) {
    if (item.plot?.chartType !== 'text') {
      continue;
    }

    const html = item.plot?.textHtml?.trim();
    const isTextPromptDefined = !!item.plot?.textPrompt?.trim();
    const isTextHtmlNotDefined = !item.plot?.textHtml?.trim();
    const isWaitingForAi = isTextPromptDefined && isTextHtmlNotDefined;

    if (isWaitingForAi) {
      pending += 1;
      continue;
    }

    if (!html) {
      continue;
    }

    const entry = textCharts.get(item.id);
    if (!entry || entry.isLoading) {
      pending += 1;
    }
  }

  const hasAiSummary = items.some((i) => i.plot?.chartType === 'ai-summary');
  if (hasAiSummary && aiSummaryState.loading) {
    pending += 1;
  }

  return pending;
}

export function pageHasTextOrAiSummaryItems(items: PositionedItem[]): boolean {
  return items.some((item) => {
    const ct = item.plot?.chartType;
    return ct === 'text' || ct === 'ai-summary';
  });
}
